import { useTranslation } from 'react-i18next';
import { speechLang } from '../../utils/narration';
import { useVoiceInput } from '../../hooks/useVoiceInput';

interface VoiceInputButtonProps {
  onTranscript: (text: string) => void;
  language?: string;
  disabled?: boolean;
}

export default function VoiceInputButton({ onTranscript, language, disabled = false }: VoiceInputButtonProps) {
  const { t, i18n } = useTranslation();
  // Falls back to the interface language rather than a fixed locale. Dictation is how a blind
  // player enters an action, and recognising Spanish speech as Russian returns nothing usable.
  const recognitionLang = language || speechLang(i18n.language);
  const {
    isListening,
    transcript,
    isSupported,
    startListening,
    stopListening,
  } = useVoiceInput({
    language: recognitionLang,
    continuous: true,
    onResult: (text) => {
      // Append each finalized phrase; keep listening for the next one.
      onTranscript(text);
    },
    onError: (error) => {
      console.error('Voice input error:', error);
      alert(error);
    },
  });

  if (!isSupported) {
    return null; // Hide button if not supported
  }

  const handleClick = () => {
    if (disabled) return;
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  return (
    <button
      // Without this the button defaults to type="submit" and, since it sits inside the
      // action <form> in TurnPanel, starting dictation submitted the half-written turn.
      type="button"
      onClick={handleClick}
      disabled={disabled}
      aria-label={isListening ? t('voiceInput.stopListeningAria') : t('voiceInput.startListeningAria')}
      title={isListening ? t('voiceInput.stopRecording') : t('voiceInput.startVoice')}
      style={{
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        border: 'none',
        background: isListening
          ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: '#fff',
        fontSize: '1.5rem',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.3s',
        boxShadow: isListening
          ? '0 0 20px rgba(239, 68, 68, 0.6)'
          : '0 4px 12px rgba(102, 126, 234, 0.3)',
        animation: isListening ? 'pulse 1.5s infinite' : 'none',
        position: 'relative',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'scale(1.1)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
      }}
    >
      {isListening ? '🎤' : '🎙️'}

      {/* Listening Indicator */}
      {isListening && (
        <div style={{
          position: 'absolute',
          top: -5,
          right: -5,
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          background: '#10b981',
          animation: 'blink 1s infinite',
        }} />
      )}

      {/* Transcript Preview */}
      {isListening && transcript && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginTop: '0.5rem',
          padding: '0.5rem 1rem',
          background: 'rgba(0, 0, 0, 0.8)',
          color: '#fff',
          borderRadius: '8px',
          fontSize: '0.85rem',
          whiteSpace: 'nowrap',
          maxWidth: '200px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {transcript}
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% {
            box-shadow: 0 0 20px rgba(239, 68, 68, 0.6);
          }
          50% {
            box-shadow: 0 0 30px rgba(239, 68, 68, 0.9);
          }
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </button>
  );
}
